/* Client-side half of Pro checkout: two thin calls to the Cloud Functions in
 * functions/index.js. All of the actual Creem API surface (the secret key,
 * building the request, reading the webhook) stays server-side — this file
 * only ever sees a checkout_url or a portal url to redirect the browser to.
 */
import { httpsCallable } from "firebase/functions";
import { functionsClient } from "./firebase.js";
import { isNative } from "./native.js";

/* Payments reopened 2026-10-01. While true, ProModal records the plan a
 * user picked in proInterest/{uid} (see proInterest.js) and tells them so,
 * instead of calling startCheckout. Flip to true to pause payments again. */
export const CHECKOUT_PAUSED = false;

/* The Android and iOS shells are CONSUMPTION-ONLY. Pro bought on the web
 * unlocks there too — the entitlement follows the uid, not the device — but
 * nothing inside a store build may start a Creem checkout, open Creem's
 * portal or quote the web price. Google Play's Payments policy and Apple's
 * guideline 3.1.1 both reserve in-app sales of digital content for the
 * store's own billing, which this app does not integrate yet (MOBILE.md §6).
 *
 * Creem went live on the web on 2026-08-22 and the shells wrap the very same
 * bundle, so until this constant existed the Android app carried a working
 * third-party checkout: the one thing a Play review rejects outright. Every
 * surface that sells or bills reads this — ProModal's plan buttons, the
 * landing page's pricing statement, Settings' "Manage subscription" — and
 * e2e/native-store-policy.spec.js runs the real bundle as a shell to pin all
 * three. The day Play Billing ships it becomes a per-platform answer rather
 * than a boolean; it does not simply flip to true. */
export const WEB_CHECKOUT = !isNative;

/* Belt and braces: the UI never offers these in a shell, and if a future
 * caller forgets to ask, the call fails here instead of at a store review. */
function assertWebCheckout() {
  if (!WEB_CHECKOUT) throw new Error("checkout is web-only: store builds are consumption-only");
}

/**
 * Starts a Creem checkout for the given plan and redirects the browser to
 * it. Throws (with a Firebase HttpsError-shaped `.code`/`.message`) if the
 * account can't check out yet — ProModal is expected to catch this and show
 * an inline error rather than let it reach the console.
 */
export async function startCheckout(plan) {
  assertWebCheckout();
  if (!functionsClient) throw new Error("no Firebase project configured");
  const create = httpsCallable(functionsClient, "createCreemCheckout");
  const successUrl = `${window.location.origin}${window.location.pathname}?pro=success`;
  const { data } = await create({ plan, successUrl });
  window.location.href = data.url;
}

/** Opens Creem's self-service billing portal for the signed-in Pro user. */
export async function openBillingPortal() {
  assertWebCheckout();
  if (!functionsClient) throw new Error("no Firebase project configured");
  const portal = httpsCallable(functionsClient, "creemBillingPortal");
  const { data } = await portal();
  window.location.href = data.url;
}
